import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Divider } from "primereact/divider";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import { useRef } from "react";
import SearchAddress from "../../../components/general/SearchAddress";
import {
  appDialogContentStyle,
  appDialogHeaderStyle,
  appDialogStyle,
} from "../../../components/general/uiSurfaceStyles";

export const CONTACT_PHONE_OR_FAX_REGEX = /^[0-9()+\-\s]{7,20}$/;
export const CONTACT_EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const validateCarePlanContactsForm = (formData: any) => {
  const normalizedContacts = {
    primaryPhysicianName: String(formData?.primaryPhysicianName || "").trim(),
    primaryPhysicianPhone: String(formData?.primaryPhysicianPhone || "").trim(),
    primaryPhysicianFax: String(formData?.primaryPhysicianFax || "").trim(),
    primaryPhysicianEmail: String(formData?.primaryPhysicianEmail || "").trim(),
    pharmacyName: String(formData?.pharmacyName || "").trim(),
    pharmacyPhone: String(formData?.pharmacyPhone || "").trim(),
    pharmacyFax: String(formData?.pharmacyFax || "").trim(),
    pharmacyEmail: String(formData?.pharmacyEmail || "").trim(),
  };

  const errors: Record<string, string> = {};

  if (!normalizedContacts.primaryPhysicianName) {
    errors.primaryPhysicianName = "Physician name is required.";
  }
  if (!normalizedContacts.pharmacyName) {
    errors.pharmacyName = "Pharmacy name is required.";
  }

  if (normalizedContacts.primaryPhysicianPhone && !CONTACT_PHONE_OR_FAX_REGEX.test(normalizedContacts.primaryPhysicianPhone)) {
    errors.primaryPhysicianPhone = "Enter a valid phone number.";
  }
  if (normalizedContacts.primaryPhysicianFax && !CONTACT_PHONE_OR_FAX_REGEX.test(normalizedContacts.primaryPhysicianFax)) {
    errors.primaryPhysicianFax = "Enter a valid fax number.";
  }
  if (normalizedContacts.primaryPhysicianEmail && !CONTACT_EMAIL_REGEX.test(normalizedContacts.primaryPhysicianEmail)) {
    errors.primaryPhysicianEmail = "Enter a valid email address.";
  }
  if (normalizedContacts.pharmacyPhone && !CONTACT_PHONE_OR_FAX_REGEX.test(normalizedContacts.pharmacyPhone)) {
    errors.pharmacyPhone = "Enter a valid phone number.";
  }
  if (normalizedContacts.pharmacyFax && !CONTACT_PHONE_OR_FAX_REGEX.test(normalizedContacts.pharmacyFax)) {
    errors.pharmacyFax = "Enter a valid fax number.";
  }
  if (normalizedContacts.pharmacyEmail && !CONTACT_EMAIL_REGEX.test(normalizedContacts.pharmacyEmail)) {
    errors.pharmacyEmail = "Enter a valid email address.";
  }

  return {
    normalizedContacts,
    errors,
  };
};

const normalizeCollateralContacts = (contacts: any[] = []) =>
  (Array.isArray(contacts) ? contacts : []).map((contact) => ({
    fullName: String(contact?.fullName || "").trim(),
    relationship: String(contact?.relationship || "").trim(),
    preferredContactMethod: String(contact?.preferredContactMethod || "").trim(),
    phoneNumber: String(contact?.phoneNumber || "").trim(),
    email: String(contact?.email || "").trim(),
  }));

type CarePlanContactsDialogProps = {
  visible: boolean;
  onHide: () => void;
  formData: any;
  setFormData: React.Dispatch<React.SetStateAction<any>>;
  errors: Record<string, string>;
  saving: boolean;
  onSave: () => void;
  header?: string;
  introText?: string;
  saveLabel?: string;
  hideSaveToCarePlanText?: boolean;
  style?: React.CSSProperties;
  contentStyle?: React.CSSProperties;
  headerStyle?: React.CSSProperties;
};

const CarePlanContactsDialog = ({
  visible,
  onHide,
  formData,
  setFormData,
  errors,
  saving,
  onSave,
  header = "Edit Care Plan Contacts",
  introText = "",
  saveLabel = "Save to Care Plan",
  hideSaveToCarePlanText = false,
  style,
  contentStyle,
  headerStyle,
}: CarePlanContactsDialogProps) => {
  const toast = useRef<Toast>(null);
  const requiredLabelStyle: React.CSSProperties = {
    color: "#b91c1c",
    fontWeight: 700,
    marginLeft: "0.25rem",
  };

  return (
    <Dialog
      header={header}
      visible={visible}
      style={style || appDialogStyle}
      headerStyle={headerStyle || appDialogHeaderStyle}
      contentStyle={contentStyle || appDialogContentStyle}
      modal
      onHide={onHide}
    >
      <Toast ref={toast} />
      {introText ? (
        <div style={{ color: "#64748b", marginBottom: "1rem", lineHeight: 1.6 }}>
          {introText}
        </div>
      ) : null}
      <div style={{ color: "#7c2d12", background: "#fff7ed", border: "1px solid #fdba74", borderRadius: "12px", padding: "0.75rem 0.9rem", marginBottom: "1rem", fontSize: "0.92rem", lineHeight: 1.5 }}>
        Required fields: <strong>Physician Name</strong> and <strong>Pharmacy Name</strong>.
      </div>
      <div className="grid">
        <div className="col-12">
          <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.35rem" }}>Primary Physician</div>
        </div>
        <div className="col-12">
          <label htmlFor="carePlanPhysicianName" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>
            Physician Name<span style={requiredLabelStyle}>*</span>
          </label>
          <InputText id="carePlanPhysicianName" value={formData?.primaryPhysicianName || ""} className="w-full" onChange={(e) => setFormData((prev: any) => ({ ...prev, primaryPhysicianName: e.target.value }))} />
          {errors?.primaryPhysicianName ? (<small style={{ color: "#b91c1c", display: "block", marginTop: "0.35rem" }}>{errors.primaryPhysicianName}</small>) : null}
        </div>
        <div className="col-12">
          <label htmlFor="carePlanPhysicianClinic" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Clinic / Practice</label>
          <InputText id="carePlanPhysicianClinic" value={formData?.primaryPhysicianClinic || ""} className="w-full" onChange={(e) => setFormData((prev: any) => ({ ...prev, primaryPhysicianClinic: e.target.value }))} />
        </div>
        <div className="col-12 md:col-6">
          <label htmlFor="carePlanPhysicianPhone" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Phone</label>
          <InputText id="carePlanPhysicianPhone" value={formData?.primaryPhysicianPhone || ""} className="w-full" onChange={(e) => setFormData((prev: any) => ({ ...prev, primaryPhysicianPhone: e.target.value }))} />
          {errors?.primaryPhysicianPhone ? (<small style={{ color: "#b91c1c", display: "block", marginTop: "0.35rem" }}>{errors.primaryPhysicianPhone}</small>) : null}
        </div>
        <div className="col-12 md:col-6">
          <label htmlFor="carePlanPhysicianFax" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Fax</label>
          <InputText id="carePlanPhysicianFax" value={formData?.primaryPhysicianFax || ""} className="w-full" onChange={(e) => setFormData((prev: any) => ({ ...prev, primaryPhysicianFax: e.target.value }))} />
          {errors?.primaryPhysicianFax ? (<small style={{ color: "#b91c1c", display: "block", marginTop: "0.35rem" }}>{errors.primaryPhysicianFax}</small>) : null}
        </div>
        <div className="col-12">
          <label htmlFor="carePlanPhysicianEmail" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Email</label>
          <InputText id="carePlanPhysicianEmail" value={formData?.primaryPhysicianEmail || ""} className="w-full" onChange={(e) => setFormData((prev: any) => ({ ...prev, primaryPhysicianEmail: e.target.value }))} />
          {errors?.primaryPhysicianEmail ? (<small style={{ color: "#b91c1c", display: "block", marginTop: "0.35rem" }}>{errors.primaryPhysicianEmail}</small>) : null}
        </div>
        <div className="col-12">
          <label htmlFor="carePlanPhysicianAddress" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Address</label>
          <SearchAddress
            id="carePlanPhysicianAddress"
            name="carePlanPhysicianAddress"
            value={formData?.primaryPhysicianAddress || ""}
            onChange={(event) => setFormData((prev: any) => ({ ...prev, primaryPhysicianAddress: String(event?.target?.value || "") }))}
            className="w-full"
          />
        </div>

        <div className="col-12"><Divider /></div>

        <div className="col-12">
          <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.35rem" }}>Primary Pharmacy</div>
        </div>
        <div className="col-12">
          <label htmlFor="carePlanPharmacyName" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>
            Pharmacy Name<span style={requiredLabelStyle}>*</span>
          </label>
          <InputText id="carePlanPharmacyName" value={formData?.pharmacyName || ""} className="w-full" onChange={(e) => setFormData((prev: any) => ({ ...prev, pharmacyName: e.target.value }))} />
          {errors?.pharmacyName ? (<small style={{ color: "#b91c1c", display: "block", marginTop: "0.35rem" }}>{errors.pharmacyName}</small>) : null}
        </div>
        <div className="col-12 md:col-6">
          <label htmlFor="carePlanPharmacyPhone" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Phone</label>
          <InputText id="carePlanPharmacyPhone" value={formData?.pharmacyPhone || ""} className="w-full" onChange={(e) => setFormData((prev: any) => ({ ...prev, pharmacyPhone: e.target.value }))} />
          {errors?.pharmacyPhone ? (<small style={{ color: "#b91c1c", display: "block", marginTop: "0.35rem" }}>{errors.pharmacyPhone}</small>) : null}
        </div>
        <div className="col-12 md:col-6">
          <label htmlFor="carePlanPharmacyFax" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Fax</label>
          <InputText id="carePlanPharmacyFax" value={formData?.pharmacyFax || ""} className="w-full" onChange={(e) => setFormData((prev: any) => ({ ...prev, pharmacyFax: e.target.value }))} />
          {errors?.pharmacyFax ? (<small style={{ color: "#b91c1c", display: "block", marginTop: "0.35rem" }}>{errors.pharmacyFax}</small>) : null}
        </div>
        <div className="col-12">
          <label htmlFor="carePlanPharmacyEmail" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Email</label>
          <InputText id="carePlanPharmacyEmail" value={formData?.pharmacyEmail || ""} className="w-full" onChange={(e) => setFormData((prev: any) => ({ ...prev, pharmacyEmail: e.target.value }))} />
          {errors?.pharmacyEmail ? (<small style={{ color: "#b91c1c", display: "block", marginTop: "0.35rem" }}>{errors.pharmacyEmail}</small>) : null}
        </div>
        <div className="col-12">
          <label htmlFor="carePlanPharmacyAddress" style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Address</label>
          <SearchAddress
            id="carePlanPharmacyAddress"
            name="carePlanPharmacyAddress"
            value={formData?.pharmacyAddress || ""}
            onChange={(event) => setFormData((prev: any) => ({ ...prev, pharmacyAddress: String(event?.target?.value || "") }))}
            className="w-full"
          />
        </div>

        <div className="col-12"><Divider /></div>

        <div className="col-12">
          <div className="flex align-items-center justify-content-between gap-2 flex-wrap">
            <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.35rem" }}>Point of Contacts</div>
            <Button
              type="button"
              label="Add Contact"
              icon="pi pi-plus"
              severity="secondary"
              outlined
              rounded
              size="small"
              onClick={() => setFormData((prev: any) => ({
                ...prev,
                collateralContacts: [
                  ...(Array.isArray(prev?.collateralContacts) ? prev.collateralContacts : []),
                  { fullName: "", relationship: "", preferredContactMethod: "", phoneNumber: "", email: "" },
                ],
              }))}
            />
          </div>
        </div>

        {normalizeCollateralContacts(formData?.collateralContacts).length > 0 ? normalizeCollateralContacts(formData?.collateralContacts).map((contact, index) => (
          <div key={`collateral-contact-${index}`} className="col-12">
            <div style={{ border: "1px solid #e2e8f0", borderRadius: "12px", padding: "0.9rem", background: "#f8fafc" }}>
              <div className="flex align-items-center justify-content-between gap-2 flex-wrap" style={{ marginBottom: "0.75rem" }}>
                <div style={{ fontWeight: 700, color: "#0f172a" }}>Contact {index + 1}</div>
                <Button
                  type="button"
                  icon="pi pi-trash"
                  label="Remove"
                  severity="danger"
                  text
                  size="small"
                  onClick={() => setFormData((prev: any) => ({
                    ...prev,
                    collateralContacts: (Array.isArray(prev?.collateralContacts) ? prev.collateralContacts : []).filter((_: any, contactIndex: number) => contactIndex !== index),
                  }))}
                />
              </div>
              <div className="grid">
                <div className="col-12 md:col-6">
                  <label htmlFor={`carePlanContactName${index}`} style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Full Name</label>
                  <InputText
                    id={`carePlanContactName${index}`}
                    value={contact.fullName}
                    className="w-full"
                    onChange={(e) => setFormData((prev: any) => {
                      const nextContacts = normalizeCollateralContacts(prev?.collateralContacts);
                      nextContacts[index] = { ...nextContacts[index], fullName: e.target.value };
                      return { ...prev, collateralContacts: nextContacts };
                    })}
                  />
                </div>
                <div className="col-12 md:col-6">
                  <label htmlFor={`carePlanContactRelationship${index}`} style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Relationship</label>
                  <InputText
                    id={`carePlanContactRelationship${index}`}
                    value={contact.relationship}
                    className="w-full"
                    onChange={(e) => setFormData((prev: any) => {
                      const nextContacts = normalizeCollateralContacts(prev?.collateralContacts);
                      nextContacts[index] = { ...nextContacts[index], relationship: e.target.value };
                      return { ...prev, collateralContacts: nextContacts };
                    })}
                  />
                </div>
                <div className="col-12 md:col-6">
                  <label htmlFor={`carePlanContactPreferred${index}`} style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Preferred Contact Method</label>
                  <InputText
                    id={`carePlanContactPreferred${index}`}
                    value={contact.preferredContactMethod}
                    className="w-full"
                    onChange={(e) => setFormData((prev: any) => {
                      const nextContacts = normalizeCollateralContacts(prev?.collateralContacts);
                      nextContacts[index] = { ...nextContacts[index], preferredContactMethod: e.target.value };
                      return { ...prev, collateralContacts: nextContacts };
                    })}
                  />
                </div>
                <div className="col-12 md:col-6">
                  <label htmlFor={`carePlanContactPhone${index}`} style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Phone</label>
                  <InputText
                    id={`carePlanContactPhone${index}`}
                    value={contact.phoneNumber}
                    className="w-full"
                    onChange={(e) => setFormData((prev: any) => {
                      const nextContacts = normalizeCollateralContacts(prev?.collateralContacts);
                      nextContacts[index] = { ...nextContacts[index], phoneNumber: e.target.value };
                      return { ...prev, collateralContacts: nextContacts };
                    })}
                  />
                </div>
                <div className="col-12">
                  <label htmlFor={`carePlanContactEmail${index}`} style={{ display: "block", fontWeight: 700, color: "#0f172a", marginBottom: "0.45rem" }}>Email</label>
                  <InputText
                    id={`carePlanContactEmail${index}`}
                    value={contact.email}
                    className="w-full"
                    onChange={(e) => setFormData((prev: any) => {
                      const nextContacts = normalizeCollateralContacts(prev?.collateralContacts);
                      nextContacts[index] = { ...nextContacts[index], email: e.target.value };
                      return { ...prev, collateralContacts: nextContacts };
                    })}
                  />
                </div>
              </div>
            </div>
          </div>
        )) : (
          <div className="col-12">
            <div style={{ color: "#64748b", fontSize: "0.9rem" }}>No point of contacts added yet.</div>
          </div>
        )}
      </div>
      <div className="mt-3 flex flex-wrap justify-content-end gap-2">
        <Button type="button" label="Cancel" severity="secondary" rounded outlined onClick={onHide} />
        <Button type="button" label={saving ? "Saving..." : saveLabel} icon="pi pi-save" severity="warning" rounded onClick={onSave} loading={saving} disabled={saving} />
      </div>
      {hideSaveToCarePlanText ? null : (
        <div className="mt-2" style={{ color: "#64748b", fontSize: "0.82rem" }}>
          These details are saved to the resident care plan contacts.
        </div>
      )}
    </Dialog>
  );
};

export default CarePlanContactsDialog;
